import React, { useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { ActivityCard, ActivityItem, DismissibleActivityCard } from "./ActivityCard";
import { Typography, Button, Flex } from "../../../components";

export interface ActivityFeedProps {
  items: ActivityItem[];
  onDismiss?: (id: string) => void;
  onUndo?: (id: string) => void;
  onItemClick?: (item: ActivityItem) => void;
  autoHideUndo?: boolean;
  undoTimeoutMs?: number;
}

const FeedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.sm};
    gap: ${({ theme }) => theme.spacing.sm};
  }

  width: 100%;
  max-width: 100%;
`;

const slideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(12px); }
`;

const UndoBar = styled(Flex)<{ disappearing?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  animation: ${({ disappearing }) => disappearing && slideOut} 0.4s ease forwards;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: row;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  items,
  onDismiss,
  onUndo,
  onItemClick,
  autoHideUndo = true,
  undoTimeoutMs = 5000,
}) => {
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [undoStack, setUndoStack] = useState<ActivityItem[]>([]);
  const [disappearing, setDisappearing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleDismiss = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    setDismissedIds((prev) => [...prev, id]);
    setUndoStack((prev) => [item, ...prev]);
    onDismiss?.(id);

    if (autoHideUndo) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setDisappearing(true);
        setTimeout(() => {
          setUndoStack((prev) => prev.slice(1));
          setDisappearing(false);
        }, 400);
      }, undoTimeoutMs);
    }
  };

  const handleUndo = () => {
    const [lastDismissed, ...rest] = undoStack;
    if (!lastDismissed) return;
    setDismissedIds((prev) => prev.filter((id) => id !== lastDismissed.id));
    setUndoStack(rest);
    onUndo?.(lastDismissed.id);
    setDisappearing(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleKeep = () => {
    setDisappearing(true);
    setTimeout(() => {
      setUndoStack((prev) => prev.slice(1));
      setDisappearing(false);
    }, 400);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  return (
    <FeedWrapper>
      {items
        .filter((item) => !dismissedIds.includes(item.id))
        .map((item) => (
          <DismissibleActivityCard key={item.id} item={item} onDismiss={handleDismiss} onClick={onItemClick} />
        ))}

      {undoStack.length > 0 && (
        <UndoBar flex="1 0 auto" disappearing={disappearing}>
          <Typography variant="caption">
            Dismissed <strong>{undoStack[0].user.name}</strong>'s activity
          </Typography>
          <Flex flex="0 1 auto" gap="sm" wrap="wrap">
            <Button size="compact" variant="text" onClick={handleUndo}>
              Undo
            </Button>
            
          </Flex>
        </UndoBar>
      )}
    </FeedWrapper>
  );
};